<script setup lang="ts">
  // Page-only hourly sales bar chart. Bars follow the business-day axis
  // (they start at the day's cutoff hour and wrap past midnight, so
  // late-night tail sales render at the END of the day); gaps between the
  // first and last active hour are zero-filled for visual continuity.
  import { computed } from 'vue';
  import { IDayCloseHourlyRow } from '@/interfaces/IDayClose';
  import { displayPrice } from '@/utils';
  import {
    buildHourlyBars,
    IHourlyBarDatum,
  } from '@/utils/day-close-hourly.utils';

  const props = defineProps<{
    hours: IDayCloseHourlyRow[];
    currencySymbol: string;
    /** Business-day start hour (payload.header.cutoffHour); 0 = calendar. */
    cutoffHour?: number;
  }>();

  interface IHourBar extends IHourlyBarDatum {
    tooltip: string;
  }

  const bars = computed<IHourBar[]>(() =>
    buildHourlyBars(props.hours, props.cutoffHour).map(bar => ({
      ...bar,
      tooltip: `${String(bar.hour).padStart(2, '0')}:00 — ${
        props.currencySymbol
      }${displayPrice(bar.amount, true)} (${bar.orders})`,
    }))
  );
</script>

<template>
  <section class="day-close-hourly-card">
    <h2 class="card-title">{{ $t('v2Stats.dayClose.hourlyTitle') }}</h2>
    <div class="hourly-chart">
      <div
        v-for="bar in bars"
        :key="bar.hour"
        class="hour-col"
        :class="{ 'hour-col--midnight': bar.wrapsMidnight }"
        :title="bar.tooltip"
      >
        <div class="hour-track">
          <div
            class="hour-fill"
            :style="{ height: Math.max(bar.heightPct, bar.amount > 0 ? 3 : 0) + '%' }"
          />
        </div>
        <span class="hour-label">{{ bar.hour }}</span>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
  .day-close-hourly-card {
    @apply tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-p-4;

    .card-title {
      @apply tw-mb-3 tw-text-sm tw-font-semibold tw-uppercase tw-tracking-wide tw-text-neutral-500;
    }

    .hourly-chart {
      @apply tw-flex tw-items-end;

      .hour-col {
        @apply tw-min-w-0 tw-flex-1 tw-px-0.5 tw-text-center;

        &--midnight {
          @apply tw-border-0 tw-border-l tw-border-dashed tw-border-neutral-300;
        }

        .hour-track {
          @apply tw-relative tw-flex tw-h-28 tw-items-end tw-overflow-hidden tw-rounded tw-bg-neutral-50;

          .hour-fill {
            @apply tw-w-full tw-rounded tw-bg-orange-500;
          }
        }

        .hour-label {
          @apply tw-mt-1 tw-block tw-text-[10px] tw-tabular-nums tw-text-neutral-400;
        }
      }
    }
  }
</style>
